import {
  doc,
  collection,
  setDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
  increment,
  query,
  where,
  getDocs,
  limit,
  arrayUnion,
  arrayRemove,
  addDoc,
  writeBatch,
  orderBy,
  serverTimestamp,
  deleteDoc,
  getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase'; // Note: changed from ../firebase to ../config/firebase to match flipbookService
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';

// Use same worker setup as pdfService/flipbookService
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;


class SlidePresentationService {
  constructor() {
    this.sessionsCollection = 'pb-slide-sessions';
    this.interactionsCollection = 'interactions';
    this.decksCollection = 'slide-decks';
    this.slidesCollection = 'slides';
  }

  // Generate a random 6-digit alphanumeric short code
  generateShortCode() {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  }

  // ==================== DECK MANAGEMENT ====================

  async getSlideDecks(bookId) {
    try {
      const decksRef = collection(db, 'pb-books', bookId, this.decksCollection);
      const q = query(decksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting slide decks:', error);
      throw error;
    }
  }

  async createSlideDeck(bookId, deckData) {
    try {
      const decksRef = collection(db, 'pb-books', bookId, this.decksCollection);
      const docRef = await addDoc(decksRef, {
        title: deckData.title || 'Untitled Deck',
        description: deckData.description || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        slideCount: 0
      });
      return { id: docRef.id, ...deckData };
    } catch (error) {
      console.error('Error creating slide deck:', error);
      throw error;
    }
  }

  async updateSlideDeck(bookId, deckId, updates) {
    try {
      const deckRef = doc(db, 'pb-books', bookId, this.decksCollection, deckId);
      await updateDoc(deckRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating slide deck:', error);
      throw error;
    }
  }

  async deleteSlideDeck(bookId, deckId) {
    try {
      const slides = await this.getSlides(bookId, deckId);
      const batch = writeBatch(db);

      // Delete all slide documents
      slides.forEach(slide => {
        const slideRef = doc(db, 'pb-books', bookId, this.decksCollection, deckId, this.slidesCollection, slide.id);
        batch.delete(slideRef);
      });

      // Delete the deck itself
      const deckRef = doc(db, 'pb-books', bookId, this.decksCollection, deckId);
      batch.delete(deckRef);

      await batch.commit();
    } catch (error) {
      console.error('Error deleting slide deck:', error);
      throw error;
    }
  }

  async getSlides(bookId, deckId) {
    try {
      const slidesRef = collection(db, 'pb-books', bookId, this.decksCollection, deckId, this.slidesCollection);
      const q = query(slidesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting slides:', error);
      throw error;
    }
  }

  async addSlide(bookId, deckId, slideData) {
    try {
      const slidesRef = collection(db, 'pb-books', bookId, this.decksCollection, deckId, this.slidesCollection);
      const docRef = await addDoc(slidesRef, {
        ...slideData,
        createdAt: serverTimestamp()
      });

      const deckRef = doc(db, 'pb-books', bookId, this.decksCollection, deckId);
      await updateDoc(deckRef, {
        slideCount: increment(1),
        updatedAt: serverTimestamp()
      });

      return { id: docRef.id, ...slideData };
    } catch (error) {
      console.error('Error adding slide:', error);
      throw error;
    }
  }

  async deleteSlide(bookId, deckId, slideId) {
    try {
      const slideRef = doc(db, 'pb-books', bookId, this.decksCollection, deckId, this.slidesCollection, slideId);
      await deleteDoc(slideRef);

      const deckRef = doc(db, 'pb-books', bookId, this.decksCollection, deckId);
      await updateDoc(deckRef, {
        slideCount: increment(-1),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting slide:', error);
      throw error;
    }
  }

  async updateSlide(bookId, deckId, slideId, updates) {
    try {
      const slideRef = doc(db, 'pb-books', bookId, this.decksCollection, deckId, this.slidesCollection, slideId);
      await updateDoc(slideRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating slide:', error);
      throw error;
    }
  }

  // ==================== PRESENTATION METHODS ====================

  // Start a live presentation session
  async startPresentation(bookId, userId, deckId, settings = {}) {
    const sessionRef = doc(collection(db, this.sessionsCollection));
    const shortCode = this.generateShortCode();

    const sessionData = {
      id: sessionRef.id,
      bookId,
      deckId,
      shortCode,
      presenterId: userId,
      startTime: serverTimestamp(),
      isLive: true,
      currentSlideIndex: 0,
      viewers: [],
      viewerCount: 0,
      settings: {
        allowQuestions: true,
        allowInsights: true,
        showPolls: true,
        recordSession: true,
        syncViewers: true,
        questionLimit: 3,
        ...settings
      },
      stats: {
        questionsAsked: 0,
        pollVotes: 0,
        mcqAnswers: 0
      }
    };

    await setDoc(sessionRef, sessionData);
    return { sessionId: sessionRef.id, shortCode };
  }

  // Join a session via short code
  async joinByCode(shortCode, userId) {
    const q = query(
      collection(db, this.sessionsCollection),
      where('shortCode', '==', shortCode),
      where('isLive', '==', true)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error('Invalid or expired short code');
    }

    const sessionDoc = snapshot.docs[0];
    await this.joinAsViewer(sessionDoc.id, userId);
    return sessionDoc.id;
  }

  // Join as viewer
  async joinAsViewer(sessionId, userId) {
    const sessionRef = doc(db, this.sessionsCollection, sessionId);
    await updateDoc(sessionRef, {
      viewers: arrayUnion(userId),
      viewerCount: increment(1)
    });

    // Caller should use this returned function to subscribe to updates
    return (callback) => onSnapshot(sessionRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data());
      }
    });
  }

  // Navigate to specific slide
  async navigateToSlide(sessionId, slideIndex) {
    const sessionRef = doc(db, this.sessionsCollection, sessionId);
    await updateDoc(sessionRef, {
      currentSlideIndex: slideIndex,
      lastNavigationTime: serverTimestamp()
    });
  }

  // Leave presentation
  async leavePresentation(sessionId, userId) {
    const sessionRef = doc(db, this.sessionsCollection, sessionId);
    await updateDoc(sessionRef, {
      viewers: arrayRemove(userId),
      viewerCount: increment(-1)
    });
  }

  // End presentation
  async endPresentation(sessionId) {
    const sessionRef = doc(db, this.sessionsCollection, sessionId);
    await updateDoc(sessionRef, {
      isLive: false,
      endTime: serverTimestamp(),
      shortCode: null
    });
  }

  // ==================== INTERACTION METHODS ====================

  async addInteraction(bookId, deckId, slideId, interactionData) {
    const interactionsRef = collection(
      db,
      'pb-books',
      bookId,
      this.decksCollection,
      deckId,
      this.slidesCollection,
      slideId,
      this.interactionsCollection
    );
    const docRef = await addDoc(interactionsRef, {
      ...interactionData,
      createdAt: serverTimestamp(),
      isActive: true
    });
    return docRef.id;
  }

  async submitResponse(sessionId, interactionId, userId, responseData) {
    const interactionRef = doc(db, this.sessionsCollection, sessionId, this.interactionsCollection, interactionId);

    if (responseData.type === 'question') {
      const sessionSnap = await getDoc(doc(db, this.sessionsCollection, sessionId));
      const settings = sessionSnap.data()?.settings;
      const limit = settings?.questionLimit || 3;

      const q = query(
        collection(db, this.sessionsCollection, sessionId, this.interactionsCollection),
        where('type', '==', 'question'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      if (snapshot.size >= limit) {
        throw new Error(`You have reached the limit of ${limit} questions.`);
      }
    }

    const responsesRef = collection(interactionRef, 'responses');
    await addDoc(responsesRef, {
      userId,
      ...responseData,
      timestamp: serverTimestamp()
    });

    const sessionRef = doc(db, this.sessionsCollection, sessionId);
    const statKey = responseData.type === 'poll' ? 'stats.pollVotes' :
      responseData.type === 'mcq' ? 'stats.mcqAnswers' :
        'stats.questionsAsked';

    await updateDoc(sessionRef, {
      [statKey]: increment(1)
    });
  }

  subscribeToInteractions(sessionId, callback) {
    const q = query(
      collection(db, this.sessionsCollection, sessionId, this.interactionsCollection),
      where('isActive', '==', true)
    );

    return onSnapshot(q, (snapshot) => {
      const interactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(interactions);
    });
  }

  subscribeToSlideInteractions(bookId, deckId, slideId, callback) {
    const q = query(
      collection(
        db,
        'pb-books',
        bookId,
        this.decksCollection,
        deckId,
        this.slidesCollection,
        slideId,
        this.interactionsCollection
      ),
      where('isActive', '==', true)
    );

    return onSnapshot(q, (snapshot) => {
      const interactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(interactions);
    });
  }

  subscribeToSession(sessionId, callback) {
    const sessionRef = doc(db, this.sessionsCollection, sessionId);
    return onSnapshot(sessionRef, (snapshot) => {
      if (snapshot.exists()) {
        callback({ id: snapshot.id, ...snapshot.data() });
      }
    });
  }

  subscribeToLiveSessionForBook(bookId, callback) {
    const q = query(
      collection(db, this.sessionsCollection),
      where('bookId', '==', bookId),
      where('isLive', '==', true),
      orderBy('startTime', 'desc'),
      limit(1)
    );

    return onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        callback(null);
        return;
      }
      const docSnap = snapshot.docs[0];
      callback({ id: docSnap.id, ...docSnap.data() });
    });
  }

  // ==================== FILE PROCESSING METHODS ====================

  async uploadSlideFile(bookId, deckId, file, onProgress = null, options = {}) {
    if (file.type === 'application/pdf') {
      if (options.keepPdf) {
        return this.processPdfSlidesAsSingleFile(bookId, deckId, file, onProgress);
      }
      return this.processPdfSlides(bookId, deckId, file, onProgress);
    } else if (file.type.startsWith('image/')) {
      return this.processImageSlide(bookId, deckId, file);
    }
    throw new Error('Unsupported file type');
  }

  async processPdfSlidesAsSingleFile(bookId, deckId, pdfFile, onProgress = null) {
    const pdfBytes = await pdfFile.arrayBuffer();
    const loadingTask = getDocument({
      data: pdfBytes,
      useWorkerFetch: false,
      isEvalSupported: false,
      useSystemFonts: true,
      disableAutoFetch: true,
      disableStream: true
    });
    const pdf = await loadingTask.promise;
    const totalPages = pdf.numPages;

    const pdfPath = `pb-books/${bookId}/decks/${deckId}/slides/deck-${Date.now()}.pdf`;
    await this.uploadPdfFile(pdfFile, pdfPath, onProgress);

    await this.updateSlideDeck(bookId, deckId, {
      renderMode: 'pdf',
      pdfStoragePath: pdfPath,
      pageCount: totalPages
    });

    const uploadedSlides = [];

    for (let i = 1; i <= totalPages; i++) {
      const slideData = await this.addSlide(bookId, deckId, {
        order: i - 1,
        type: 'pdf',
        pageNumber: i
      });
      uploadedSlides.push(slideData);

      if (onProgress) {
        onProgress({
          current: i,
          total: totalPages,
          percentage: Math.round((i / totalPages) * 100),
          label: 'Indexing PDF pages...'
        });
      }
    }

    await pdf.destroy();
    return uploadedSlides;
  }

  async processPdfSlides(bookId, deckId, pdfFile, onProgress = null) {
    const pdfBytes = await pdfFile.arrayBuffer();
    const loadingTask = getDocument({ data: pdfBytes });
    const pdf = await loadingTask.promise;
    const totalPages = pdf.numPages;
    const uploadedSlides = [];

    for (let i = 1; i <= totalPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 2.0 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({ canvasContext: context, viewport }).promise;
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));

      const path = `pb-books/${bookId}/decks/${deckId}/slides/slide-${Date.now()}-${i}.jpg`;
      const url = await this.uploadImage(blob, path);

      const slideData = await this.addSlide(bookId, deckId, {
        url,
        order: i - 1,
        type: 'image'
      });

      uploadedSlides.push(slideData);

      if (onProgress) {
        onProgress({ current: i, total: totalPages, percentage: Math.round((i / totalPages) * 100) });
      }
    }
    return uploadedSlides;
  }

  async processImageSlide(bookId, deckId, imageFile) {
    const path = `pb-books/${bookId}/decks/${deckId}/slides/slide-${Date.now()}-${imageFile.name}`;
    const url = await this.uploadImage(imageFile, path);
    const slideData = await this.addSlide(bookId, deckId, {
      url,
      order: 0,
      type: 'image'
    });
    return [slideData];
  }

  async uploadImage(blob, path) {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, blob);
    return getDownloadURL(storageRef);
  }

  async getDownloadUrlFromStoragePath(storagePath) {
    if (!storagePath) return null;
    const storageRef = ref(storage, storagePath);
    return getDownloadURL(storageRef);
  }

  async uploadPdfFile(pdfFile, path, onProgress = null) {
    const storageRef = ref(storage, path);

    if (!onProgress) {
      await uploadBytes(storageRef, pdfFile);
      return getDownloadURL(storageRef);
    }

    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, pdfFile);
      task.on(
        'state_changed',
        (snapshot) => {
          const percentage = snapshot.totalBytes
            ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            : 0;
          onProgress({ percentage, label: 'Uploading PDF...' });
        },
        (error) => reject(error),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  }
}


export const slidesService = new SlidePresentationService();
export default slidesService;
